import { Button, Chip, Tab, Tabs, useDisclosure } from "@heroui/react";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { Ticket, TicketPriority, TicketStatus } from "@/api/tickets/tickets.ts";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { CreateTicketModal } from "@/app/components/modals/ticket/createTicketModal";
import { FaArrowRight, FaPencil, FaPlus } from "react-icons/fa6";
import { EditTicketModal } from "@/app/components/modals/ticket/editTicketModal";
import { useNavigate } from "react-router-dom";
import useErrorHandling from "@/hooks/useErrorHandling";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { PLAN_FEATURES } from "@/app/store/plans/constants";
import { useTour } from "@/hooks/useTour";

export function TicketsPage() {
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [rawTickets, setRawTickets] = useState<Ticket[] | null>(null);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [activeStatus, setActiveStatus] = useState<string>("all");
  const [activePriority, setActivePriority] = useState<string>("all");
  const navigate = useNavigate();

  const handleError = useErrorHandling();

  const createTicketModalDisclosure = useDisclosure();
  const editTicketModalDisclosure = useDisclosure();

  const fullscreenDisclosure = useDisclosure();

  useTour(8);

  const editTicket = (ticket: Ticket) => {
    setTicket(ticket);
    editTicketModalDisclosure.onOpen();
  };

  useEffect(() => {
    setTickets(
      rawTickets?.filter((ticket) => {
        if (activeStatus == "all") return true;
        if (activeStatus == "open") return ticket.status == "open";
        if (activeStatus == "in_progress")
          return ticket.status == "in_progress";
        if (activeStatus == "closed") return ticket.status == "closed";
      }) || [],
    );
  }, [activeStatus, rawTickets]);

  useEffect(() => {
    api.tickets
      .list(
        activeTab == "platform" ? "to_platform" : "user_to_landlord",
        undefined,
        activePriority == "all"
          ? undefined
          : (activePriority as TicketPriority),
      )
      .then((response) => {
        setRawTickets(response.data);
      })
      .catch((err) => {
        toast.error("Произошла ошибке при загрузке тикетов!");
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [
    activeTab,
    activePriority,
    createTicketModalDisclosure.isOpen,
    editTicketModalDisclosure.isOpen,
    handleError,
    activeStatus,
  ]);

  const fields = useMemo(() => {
    return [
      {
        key: "subject",
        label: "Тема обращения",
        type: ColumnType.String,
        sortable: true,
      },
      {
        key: "priority",
        label: "Приоритет",
        type: ColumnType.Custom,
        sortable: true,
        render(value: TicketPriority) {
          return (
            <Chip
              color={
                value == "low"
                  ? "default"
                  : value == "medium"
                    ? "warning"
                    : value == "high"
                      ? "danger"
                      : value == "urgent"
                        ? "danger"
                        : "default"
              }
              variant="bordered">
              {value == "low"
                ? "Низкий"
                : value == "medium"
                  ? "Средний"
                  : value == "high"
                    ? "Высокий"
                    : "Срочный"}
            </Chip>
          );
        },
      },
      {
        key: "status",
        label: "Статус",
        type: ColumnType.Custom,
        sortable: true,
        render(value: TicketStatus) {
          return (
            <Chip
              color={
                value == "open"
                  ? "default"
                  : value == "in_progress"
                    ? "success"
                    : "default"
              }
              variant="bordered">
              {value == "open"
                ? "Открыт"
                : value == "in_progress"
                  ? "В работе"
                  : "Закрыт"}
            </Chip>
          );
        },
      },
      {
        key: "category",
        label: "Категория",
        type: ColumnType.Category,
        sortable: true,
      },
      ...(activeTab == "all"
        ? [
            {
              key: "renter_id",
              label: "Компания",
              type: ColumnType.Renter,
            },
            {
              key: "office_id",
              label: "Офис",
              type: ColumnType.Office,
            },
          ]
        : []),
      {
        key: "created_at",
        label: "Создан",
        type: ColumnType.DateTime,
        sortable: true,
      },
      {
        key: "actions",
        label: "Действия",
        type: ColumnType.Actions,
        actions: [
          {
            icon: <FaArrowRight />,
            onClick: (_value: any, row: any) => {
              navigate(`/dashboard/tickets/${row.type}/${row.id}`);
            },
          },
          {
            icon: <FaPencil />,
            onClick: (_value: any, row: any) => {
              editTicket(row);
            },
            planPermission: PLAN_FEATURES.TICKETS,
          },
        ],
      },
    ];
  }, [activeTab]);

  return (
    <>
      <CreateTicketModal disclosure={createTicketModalDisclosure} />
      <EditTicketModal disclosure={editTicketModalDisclosure} ticket={ticket} />

      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium" id="tickets-title">
                Тикеты
              </span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaPlus />}
                color="primary"
                variant="solid"
                className="max-w-fit"
                id="create-ticket-button"
                onClick={() => createTicketModalDisclosure.onOpen()}>
                Новый тикет
              </Button>
            </div>
          </div>
          <div className="flex flex-row flex-wrap gap-4 mb-4">
            <Tabs
              color="primary"
              variant="bordered"
              selectedKey={activeTab}
              onSelectionChange={(key) => setActiveTab(key as string)}>
              <Tab key="all" title="Бизнес" />
              <Tab key="platform" title="Платформа" />
            </Tabs>

            <Tabs
              color="primary"
              variant="bordered"
              selectedKey={activeStatus}
              onSelectionChange={(key) => setActiveStatus(key as string)}>
              <Tab key="all" title="Все статусы" />
              <Tab key="open" title="Открытые" />
              <Tab key="in_progress" title="В работе" />
              <Tab key="closed" title="Закрытые" />
            </Tabs>

            <Tabs
              color="primary"
              variant="bordered"
              selectedKey={activePriority}
              onSelectionChange={(key) => setActivePriority(key as string)}>
              <Tab key="all" title="Все приоритеты" />
              <Tab key="low" title="Низкий" />
              <Tab key="medium" title="Средний" />
              <Tab key="high" title="Высокий" />
              <Tab key="urgent" title="Срочный" />
            </Tabs>
          </div>
          <div className="flex flex-col mb-2 gap-2">
            <div id="tickets-table">
              <TableBuilder
                fullscreenDisclosure={fullscreenDisclosure}
                displayOptions={false}
                removeWrapper
                rowsPerPage={10}
                columns={fields}
                data={tickets || []}
                defaultSortDescriptor={{
                  column: "priority",
                  direction: "ascending",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
